import { COLORS } from '../helpers/constants';

/**
 * Single source of truth for how competencies are labelled and coloured in the
 * web results view. Mirrors `competencyCatalog.ts` in the backend, which owns
 * the scoring contract: the keys here are the keys of `filteredPointsTest`.
 *
 * Adding a competency means adding an entry — no changes to the view.
 */

export type ColorAcronym = 'AM' | 'V' | 'R' | 'AZ';

export interface CompetencyMeta {
  key: string;
  label: string;
  color: string;
  colorCode: ColorAcronym;
  backgroundColor: string;
  badgeTextColor: string;
  /** DISC pattern name, used as the chart group label. */
  group: string;
  /**
   * Nombre de la competencia tal como vive en `test_sections.description`. Es la
   * clave de join del contrato con el back (`selectedCompetences`, #63), y no
   * siempre coincide con `key`, que es la clave de `filteredPointsTest`.
   */
  sectionDescription: string;
  /** Definición de negocio, mostrada en el selector de competencias (#66). */
  definition: string;
  /** "Elementos que evalúa", mostrados en la tarjeta del selector (#66). */
  elements: string[];
}

/**
 * Nombre de negocio de cada estilo DISC, por acrónimo de color. Fuente única:
 * lo consumen el catálogo de competencias (como rótulo de grupo), el gráfico DISC
 * "Su estilo Adaptado" y el interpretador de colores de cargos. Renombrar un
 * estilo se hace acá y en ningún otro lugar (Singulares/front#40).
 */
export const DISC_STYLE_NAMES: Record<ColorAcronym, string> = {
  R:  'Resolutivo',
  AM: 'Influenciador',
  V:  'Consejero',
  AZ: 'Perfeccionista',
};

/**
 * Nombre del color de cada estilo DISC, por acrónimo. Es el rótulo que acompaña
 * al nombre del estilo en la tabla del selector de modo de evaluación.
 */
export const DISC_COLOR_LABELS: Record<ColorAcronym, string> = {
  R:  'Rojo',
  AM: 'Amarillo',
  V:  'Verde',
  AZ: 'Azul',
};

/** Color de cargo (como llega desde el back) → acrónimo DISC. */
export const COLOR_NAME_TO_ACRONYM: Record<string, ColorAcronym> = {
  rojo: 'R',
  amarillo: 'AM',
  verde: 'V',
  azul: 'AZ',
};

const PALETTE: Record<
  ColorAcronym,
  { color: string; backgroundColor: string; badgeTextColor: string; group: string }
> = {
  R:  { color: COLORS.red,    backgroundColor: 'rgba(238, 102, 102, 0.2)', badgeTextColor: COLORS.burgundy,   group: DISC_STYLE_NAMES.R },
  AM: { color: COLORS.yellow, backgroundColor: 'rgba(250, 200, 88, 0.2)',  badgeTextColor: COLORS.darkyellow, group: DISC_STYLE_NAMES.AM },
  V:  { color: COLORS.green,  backgroundColor: 'rgba(145, 204, 117, 0.2)', badgeTextColor: COLORS.green,      group: DISC_STYLE_NAMES.V },
  AZ: { color: COLORS.blue,   backgroundColor: 'rgba(84, 112, 198, 0.2)',  badgeTextColor: COLORS.blue,       group: DISC_STYLE_NAMES.AZ },
};

const define = (
  key: string,
  label: string,
  colorCode: ColorAcronym,
  definition: string,
  elements: string[],
  sectionDescription: string = label,
): CompetencyMeta => ({
  key,
  label,
  colorCode,
  definition,
  elements,
  sectionDescription,
  ...PALETTE[colorCode],
});

/**
 * Order matters: it drives the display order of the results view, grouping
 * competencies by DISC colour.
 *
 * Las definiciones y los "elementos que evalúa" son los del catálogo acordado en
 * la issue #17.
 *
 * Ojo con el color de las 8 nuevas: acá se asigna para poder agrupar y filtrar en
 * el selector (#66), pero en el back siguen con `colorAcronym: null` hasta #69.
 * Esa asimetría es deliberada — es lo que mantiene el modo por patrón evaluando
 * exactamente las mismas competencias que hoy.
 */
export const COMPETENCY_CATALOG: CompetencyMeta[] = [
  define('Determinacion', 'Determinación', 'R',
    'Capacidad para actuar con energía, adaptabilidad, agilidad, determinación y proactividad en la realización de tareas o el logro de objetivos, manteniendo una alta dedicación y enfoque.',
    ['Energía', 'Rendimiento y rapidez', 'Proactividad']),
  define('OrientacionResultados', 'Orientación a Resultados', 'R',
    'Capacidad para enfocar los esfuerzos y acciones hacia el logro de metas específicas y objetivos previamente establecidos, manteniendo el foco en la eficiencia y en el impacto positivo de sus tareas.',
    ['Logro de metas ambiciosas', 'Adaptación ante desafíos', 'Superación y altos estándares']),
  define('Proactividad', 'Proactividad', 'R',
    'Capacidad de anticiparse a los acontecimientos, actuar con iniciativa propia y generar acciones sin necesidad de instrucciones explícitas, asumiendo responsabilidad y buscando mejoras continuas.',
    ['Iniciativa y anticipación', 'Orientación a la mejora y responsabilidad personal']),
  define('ResolucionProblemas', 'Resolución de Problemas', 'R',
    'Capacidad de identificar, analizar y resolver de manera eficaz situaciones complejas o desafíos emergentes, evaluando alternativas y ejecutando soluciones eficientes.',
    ['Análisis e identificación del problema', 'Evaluación de alternativas y toma de decisiones', 'Implementación de soluciones y uso de recursos']),

  define('ImpactoInfluencia', 'Impacto e Influencia', 'AM',
    'Capacidad de persuadir, motivar y guiar a otros hacia una idea, decisión o acción deseada, utilizando comunicación estratégica, empatía y la construcción de relaciones, ajustando el estilo según la audiencia.',
    ['Intención de influir', 'Capacidad de influir', 'Flexibilidad', 'Alianzas', 'Credibilidad']),
  define('Innovacion', 'Innovación', 'AM',
    'Capacidad para generar, aplicar y adaptar ideas, métodos, productos o servicios novedosos que mejoren procesos, resuelvan problemas o aporten valor. Implica pensamiento creativo y disposición a probar nuevas ideas.',
    ['Generar nuevas ideas', 'Experimentar cosas nuevas', 'Creación colaborativa', 'Visión global y conexión de ideas']),
  define('DesarrolloRelaciones', 'Desarrollo de Relaciones', 'AM',
    'Capacidad de establecer, mantener y fortalecer relaciones interpersonales de calidad, basadas en la confianza, la empatía y la comunicación efectiva.',
    ['Gestión de relaciones interpersonales', 'Desarrollo de redes y vínculos estratégicos']),
  define('Negociacion', 'Negociación', 'AM',
    'Habilidad para gestionar acuerdos a través de la comunicación efectiva, la comprensión de intereses propios y ajenos, y la generación de soluciones mutuamente beneficiosas.',
    ['Claridad de objetivo y capacidad de persuadir', 'Escucha activa y comprensión de intereses', 'Flexibilidad y adaptabilidad']),

  define('InteligenciaEmocional', 'Inteligencia Emocional', 'V',
    'Capacidad de reconocer, comprender y gestionar las propias emociones y las de los demás. Implica autocontrol, empatía, autoconciencia y la gestión de las relaciones interpersonales.',
    ['Regulación emocional', 'Empatía y comunicación', 'Generar ambientes positivos y armoniosos', 'Confianza y apoyo']),
  define('VisionEstrategica', 'Visión Estratégica', 'V',
    'Habilidad para comprender rápidamente los cambios del entorno, las oportunidades del mercado, las amenazas competitivas y las fortalezas y debilidades de la propia organización, para identificar la mejor respuesta a largo plazo.',
    ['Comprensión del entorno', 'Detección de oportunidades innovadoras', 'Comunicación y alineación de la visión']),
  define('OrientacionServicio', 'Orientación de Servicio', 'V',
    'Disposición activa para identificar, comprender y satisfacer las necesidades de clientes internos o externos, a través de respuestas ágiles, empáticas y de calidad.',
    ['Actitud colaborativa', 'Comprender necesidades', 'Satisfacer necesidades']),
  define('Confiabilidad', 'Confiabilidad', 'V',
    'Capacidad de actuar con integridad, responsabilidad y consistencia, cumpliendo los compromisos asumidos y generando confianza en el entorno laboral.',
    ['Cumplimiento de compromisos', 'Predictibilidad y consistencia', 'Coherencia']),

  define('Responsabilidad', 'Responsabilidad', 'AZ',
    'Capacidad de asumir, cumplir y responder por los compromisos y obligaciones asignados, demostrando integridad, disciplina y compromiso en la ejecución de tareas y proyectos.',
    ['Método y cumplimiento', 'Manejo de errores', 'Disciplina', 'Principio rector']),
  define('Estructura', 'Estructura', 'AZ',
    'Capacidad de organizar, sistematizar y dar coherencia a los elementos de una tarea, planificando, jerarquizando información y estableciendo procesos o secuencias lógicas para alcanzar objetivos de manera eficiente.',
    ['Planificación detallada', 'Mejora continua', 'Organización de información y datos', 'Gestión del riesgo']),
  define('OrganizacionPlanificacion', 'Organización y Planificación', 'AZ',
    'Capacidad de estructurar actividades, definir prioridades y administrar de manera eficiente los recursos disponibles para alcanzar objetivos en tiempo y forma.',
    ['Visión global y establecimiento de prioridades', 'Orden y organización del trabajo', 'Previsión y eficiencia operativa']),
  define('PensamientoAnalitico', 'Pensamiento Analítico', 'AZ',
    'Capacidad para analizar información de manera lógica y estructurada, descomponiendo situaciones complejas para identificar relaciones, patrones, causas y consecuencias.',
    ['Relación y análisis de variables', 'Identificación de patrones y tendencias', 'Análisis de causas y consecuencias']),
];

export interface DiscStyleGroup {
  colorCode: ColorAcronym;
  /** Nombre de negocio del estilo, p.ej. "Resolutivo". */
  styleName: string;
  /** Nombre del color del estilo, p.ej. "Rojo". */
  colorLabel: string;
  color: string;
  backgroundColor: string;
  badgeTextColor: string;
  competencies: CompetencyMeta[];
}

/**
 * Las competencias agrupadas por estilo DISC, respetando el orden del catálogo
 * Insumo de la tabla de estilos del selector de modo de evaluación:.
  */
export const competenciesByStyle = (): DiscStyleGroup[] => {
  const groups: DiscStyleGroup[] = [];

  for (const competency of COMPETENCY_CATALOG) {
    const { colorCode } = competency;
    let group = groups.find(g => g.colorCode === colorCode);

    if (!group) {
      const { color, backgroundColor, badgeTextColor } = PALETTE[colorCode];
      group = {
        colorCode,
        styleName: DISC_STYLE_NAMES[colorCode],
        colorLabel: DISC_COLOR_LABELS[colorCode],
        color,
        backgroundColor,
        badgeTextColor,
        competencies: [],
      };
      groups.push(group);
    }

    group.competencies.push(competency);
  }

  return groups;
};

/** #16 criterio 2: el modo "competencias específicas" evalúa exactamente 8 de las 16. */
export const REQUIRED_COMPETENCY_SELECTION = 8;

const BY_KEY = new Map(COMPETENCY_CATALOG.map(c => [c.key, c]));

/** Readable fallback so an unknown competency is still rendered, never dropped. */
const fallbackFor = (key: string): CompetencyMeta => ({
  key,
  label: key.replace(/([a-z])([A-Z])/g, '$1 $2'),
  color: COLORS.petrol,
  colorCode: 'AZ',
  backgroundColor: 'rgba(0, 158, 158, 0.2)',
  badgeTextColor: COLORS.petrol,
  group: 'Otras',
  sectionDescription: key,
  definition: '',
  elements: [],
});

/** Never returns undefined: unknown keys get a fallback rather than disappearing. */
export const metaForCompetency = (key: string): CompetencyMeta => BY_KEY.get(key) ?? fallbackFor(key);

export const isKnownCompetency = (key: string): boolean => BY_KEY.has(key);

/**
 * Orders results by the catalog, keeping any key not present in it at the end
 * so new competencies are never silently discarded.
 */
export const sortResultsByCatalog = <T>(results: Record<string, T> | null | undefined) => {
  if (!results) return results;

  const ordered: Record<string, T> = {};

  for (const { key } of COMPETENCY_CATALOG) {
    if (results[key]) ordered[key] = results[key];
  }

  for (const key of Object.keys(results)) {
    if (!ordered[key] && results[key]) ordered[key] = results[key];
  }

  return ordered;
};
